import { useState } from "react";
import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { API, inr, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { LibraryBig, ShoppingBag, Download, User, LogOut, Package, Loader2, FileText } from "lucide-react";

const TABS = [
  { to: "/account", label: "Overview", icon: User, end: true },
  { to: "/account/orders", label: "My Orders", icon: ShoppingBag },
  { to: "/account/library", label: "My Library", icon: LibraryBig },
  { to: "/account/downloads", label: "Downloads", icon: Download },
  { to: "/account/profile", label: "Profile", icon: User },
];

function StatusBadge({ status }) {
  const map = { PAID: "bg-teal-50 text-teal-700 border-teal-200", DIGITAL_DELIVERED: "bg-teal-50 text-teal-700 border-teal-200",
    COMPLETED: "bg-teal-50 text-teal-700 border-teal-200", PROCESSING: "bg-blue-50 text-blue-700 border-blue-200",
    SHIPPED: "bg-blue-50 text-blue-700 border-blue-200", DELIVERED: "bg-teal-50 text-teal-700 border-teal-200",
    PAYMENT_PENDING: "bg-amber-50 text-amber-700 border-amber-200", REFUNDED: "bg-red-50 text-red-700 border-red-200" };
  return <Badge className={`${map[status] || "bg-slate-100 text-slate-600 border-slate-200"} border text-xs`}>{status?.replace(/_/g, " ")}</Badge>;
}

function Overview() {
  const { user } = useAuth();
  const { data: orders } = useQuery({ queryKey: ["myorders"], queryFn: async () => (await api.get("/orders")).data });
  const { data: lib } = useQuery({ queryKey: ["mylib"], queryFn: async () => (await api.get("/library")).data });
  return (
    <div>
      <h2 className="text-xl font-bold text-navy mb-1">Welcome back, {user?.name?.split(" ")[0]}</h2>
      <p className="text-slate-500 text-sm mb-6">Here's a quick look at your account.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5"><ShoppingBag className="w-6 h-6 text-teal mb-2" /><div className="text-2xl font-bold text-navy">{orders?.length || 0}</div><div className="text-xs text-slate-500">Orders</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-5"><LibraryBig className="w-6 h-6 text-teal mb-2" /><div className="text-2xl font-bold text-navy">{lib?.length || 0}</div><div className="text-xs text-slate-500">Library items</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-5"><User className="w-6 h-6 text-teal mb-2" /><div className="text-2xl font-bold text-navy">{inr(user?.total_spend || 0)}</div><div className="text-xs text-slate-500">Total spend</div></div>
      </div>
    </div>
  );
}

function Orders() {
  const { data: orders, isLoading } = useQuery({ queryKey: ["myorders"], queryFn: async () => (await api.get("/orders")).data });
  if (isLoading) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  if (!orders?.length) return <p className="text-slate-500">You haven't placed any orders yet.</p>;
  return (
    <div className="space-y-3">
      <h2 className="text-xl font-bold text-navy mb-2">My Orders</h2>
      {orders.map((o) => (
        <div key={o.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`order-row-${o.order_no}`}>
          <div className="flex justify-between items-start gap-3">
            <div>
              <div className="font-mono font-semibold text-navy text-sm">{o.order_no}</div>
              <div className="text-xs text-slate-400 mt-0.5">{new Date(o.created_at).toLocaleString("en-IN")}</div>
            </div>
            <div className="text-right"><div className="font-bold text-navy">{inr(o.total)}</div><StatusBadge status={o.status} /></div>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100 text-sm text-slate-600">
            {o.items.map((i) => <div key={i.product_id} className="flex justify-between"><span>{i.name} × {i.quantity}</span><span>{inr(i.line_total)}</span></div>)}
          </div>
          {o.invoice_id && (
            <div className="mt-2 flex items-center gap-4">
              <a href={`${API}/invoices/${o.invoice_id}/pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-teal text-xs font-medium hover:text-teal-dark" data-testid={`invoice-pdf-${o.order_no}`}><Download className="w-3 h-3" /> Download PDF invoice</a>
              <a href={`${API}/invoices/${o.invoice_id}/view`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-slate-500 text-xs font-medium hover:text-slate-700" data-testid={`invoice-link-${o.order_no}`}><FileText className="w-3 h-3" /> View</a>
            </div>
          )}
          {o.tracking_number && <div className="mt-2 text-xs text-blue-700 flex items-center gap-1"><Package className="w-3 h-3" /> Tracking: {o.tracking_number}</div>}
        </div>
      ))}
    </div>
  );
}

function Library() {
  const { data: lib, isLoading } = useQuery({ queryKey: ["mylib"], queryFn: async () => (await api.get("/library")).data });
  const [dl, setDl] = useState(null);
  const download = async (ent) => {
    setDl(ent.id);
    try {
      const res = await api.get(`/library/download/${ent.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = ent.product_slug + (res.data.type === "text/plain" ? ".txt" : ".pdf");
      a.click(); URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) { toast.error("Download failed. Please contact support."); }
    finally { setDl(null); }
  };
  if (isLoading) return <Loader2 className="w-6 h-6 animate-spin text-teal" />;
  if (!lib?.length) return <p className="text-slate-500">Your library is empty. Purchased digital products appear here instantly.</p>;
  return (
    <div>
      <h2 className="text-xl font-bold text-navy mb-4">My Digital Library</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {lib.map((ent) => (
          <div key={ent.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden flex" data-testid={`library-item-${ent.product_slug}`}>
            <div className="w-24 bg-slate-100 shrink-0">{ent.cover_image && <img src={ent.cover_image} alt="" className="w-full h-full object-cover" />}</div>
            <div className="p-4 flex-1">
              <h3 className="font-heading font-semibold text-navy text-sm leading-snug">{ent.product_name}</h3>
              <div className="text-xs text-slate-400 mt-1">v{ent.version} · {ent.order_no}</div>
              <div className="text-xs text-slate-400">Purchased {new Date(ent.granted_at).toLocaleDateString("en-IN")}</div>
              <Button size="sm" className="mt-3 bg-teal hover:bg-teal-dark text-white gap-1" onClick={() => download(ent)} disabled={dl === ent.id} data-testid={`download-${ent.product_slug}`}>
                {dl === ent.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Downloads() {
  const { data: lib } = useQuery({ queryKey: ["mylib"], queryFn: async () => (await api.get("/library")).data });
  return (
    <div>
      <h2 className="text-xl font-bold text-navy mb-4">Downloads & Access Log</h2>
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
        {(lib || []).map((ent) => (
          <div key={ent.id} className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3"><FileText className="w-5 h-5 text-teal" />
              <div><div className="font-medium text-navy text-sm">{ent.product_name}</div>
                <div className="text-xs text-slate-400">Downloaded {ent.download_count || 0} time(s){ent.last_download_at ? ` · last ${new Date(ent.last_download_at).toLocaleDateString("en-IN")}` : ""}</div></div>
            </div>
            <Badge className="bg-teal-50 text-teal-700 border border-teal-200 text-xs">{ent.status}</Badge>
          </div>
        ))}
        {!lib?.length && <p className="p-4 text-slate-500 text-sm">No downloadable products yet.</p>}
      </div>
    </div>
  );
}

function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", mobile: user?.mobile || "", city: user?.city || "", state: user?.state || "", business_name: user?.business_name || "", business_type: user?.business_type || "" });
  const [loading, setLoading] = useState(false);
  const save = async () => {
    setLoading(true);
    try { const { data } = await api.put("/account/profile", form); setUser(data); toast.success("Profile updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <div>
      <h2 className="text-xl font-bold text-navy mb-4">Profile & Preferences</h2>
      <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-xl">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" /></div>
          <div><Label className="text-xs">Email</Label><Input value={user?.email} disabled /></div>
          <div><Label className="text-xs">Mobile / WhatsApp</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="profile-mobile" /></div>
          <div><Label className="text-xs">City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
          <div><Label className="text-xs">Business name</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} /></div>
          <div><Label className="text-xs">Business type</Label><Input value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })} /></div>
        </div>
        <Button className="mt-4 bg-teal hover:bg-teal-dark text-white" onClick={save} disabled={loading} data-testid="profile-save">Save changes</Button>
      </div>
    </div>
  );
}

export default function Account() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/"); };
  return (
    <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          <aside className="lg:col-span-1">
            <nav className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar bg-white border border-slate-200 rounded-xl p-2">
              {TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <NavLink key={t.to} to={t.to} end={t.end} data-testid={`account-tab-${t.label.toLowerCase().replace(/ /g, "-")}`}
                    className={({ isActive }) => `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap ${isActive ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                    <Icon className="w-4 h-4" /> {t.label}
                  </NavLink>
                );
              })}
              <button onClick={doLogout} className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50" data-testid="logout-btn">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </nav>
          </aside>
          <section className="lg:col-span-3">
            <Routes>
              <Route index element={<Overview />} />
              <Route path="orders" element={<Orders />} />
              <Route path="library" element={<Library />} />
              <Route path="downloads" element={<Downloads />} />
              <Route path="profile" element={<Profile />} />
            </Routes>
          </section>
        </div>
    </div>
  );
}
